import { useState, useEffect, useMemo } from 'react';
import { Users, Star, Phone, CheckCircle, Plus, X, DollarSign, Bell, Trophy, Edit, Trash2, Save, Search } from 'lucide-react';
import { mecanicosApi, adelantosApi, planillasApi, cajaChicaApi } from '../lib/api';
import { fechaLima } from '../lib/fechas';

interface Mecanico {
  id: string;
  nombre: string;
  documento: string;
  telefono: string;
  calificacion: number;
  disponible: boolean;
  estado: string;
  activo: boolean;
  servicios_completados: number;
  sueldo_base: number;
  ranking: number;
  asistencias: { fecha: string; presente: boolean }[];
  ultima_asistencia?: string;
  dias_trabajados_mes?: number;
}

const MecanicosCompleto = () => {
  const [mecanicos, setMecanicos] = useState<Mecanico[]>([]);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [mostrarAsistencia, setMostrarAsistencia] = useState(false);
  const [mostrarAlerta, setMostrarAlerta] = useState(false);
  const [mensajeAlerta, setMensajeAlerta] = useState('');
  const [vistaActual, setVistaActual] = useState<'lista' | 'adelantos' | 'asistencia' | 'calendario' | 'pagos'>('lista');
  
  // Estados para edición
  const [editandoMecanico, setEditandoMecanico] = useState<Mecanico | null>(null);
  const [mostrarFormularioEdicion, setMostrarFormularioEdicion] = useState(false);
  
  // Estado para búsqueda
  const [busqueda, setBusqueda] = useState('');
  
  // Estados para sincronización
  const [ultimaActualizacion, setUltimaActualizacion] = useState(new Date());
  const [estadoConexion, setEstadoConexion] = useState<'conectado' | 'desconectado' | 'sincronizando'>('conectado');
  
  const [adelantos, setAdelantos] = useState<any[]>([]);
  const [nuevoMecanico, setNuevoMecanico] = useState({
    nombre: '',
    documento: '',
    telefono: '',
    calificacion: 4.0,
    disponible: true,
    estado: 'activo',
    servicios_completados: 0,
    sueldo_base: 1500
  });

  useEffect(() => {
    cargarMecanicos();
    cargarAdelantos();
    verificarAlertas();

    // Verificar alertas cada hora
    const intervaloAlertas = setInterval(verificarAlertas, 3600000);

    // Sincronización periódica cada 30 segundos (ya no hay suscripción realtime de Supabase)
    const intervaloSincronizacion = setInterval(() => {
      setEstadoConexion('sincronizando');
      cargarMecanicos().then(() => {
        setUltimaActualizacion(new Date());
        setEstadoConexion('conectado');
      });
    }, 30000); // 30 segundos

    return () => {
      clearInterval(intervaloAlertas);
      clearInterval(intervaloSincronizacion);
    };
  }, []);

  // Función para sanitizar asistencias
  const sanitizarAsistencias = (asistencias: any): { fecha: string; presente: boolean; estado?: string }[] => {
    if (!asistencias) return [];
    if (Array.isArray(asistencias)) {
      // Asegurar retrocompatibilidad agregando 'estado' si no existe
      return asistencias.map(a => ({
        fecha: a.fecha,
        presente: a.presente,
        estado: a.estado || (a.presente ? 'presente' : 'ausente')
      }));
    }
    if (typeof asistencias === 'string') {
      try {
        const parsed = JSON.parse(asistencias);
        if (Array.isArray(parsed)) {
          return parsed.map(a => ({
            fecha: a.fecha,
            presente: a.presente,
            estado: a.estado || (a.presente ? 'presente' : 'ausente')
          }));
        }
        return [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const cargarMecanicos = async () => {
    try {
      console.log('🔄 Cargando mecánicos...');
      const data = await mecanicosApi.getAll();

      if (data && data.length > 0) {
        // Limpiar datos de sueldos corruptos y sanitizar asistencias
        const dataSaneada = data.map(mecanico => ({
          ...mecanico,
          sueldo_base: isNaN(mecanico.sueldo_base) ? 1500 : mecanico.sueldo_base,
          asistencias: sanitizarAsistencias(mecanico.asistencias)
        }));
        
        const mecanicosConRanking = calcularRanking(dataSaneada);
        setMecanicos(mecanicosConRanking);
        console.log(`✅ ${mecanicosConRanking.length} mecánicos cargados correctamente`);
        
        // Corregir datos corruptos en la base de datos si es necesario
        await corregirSueldosCorruptos(data);
      } else {
        // Si no hay datos, crear ejemplos iniciales
        await crearMecanicosEjemplo();
      }
    } catch (error) {
      console.error('❌ Error cargando mecánicos:', error);
    }
  };
  const cargarAdelantos = async () => {
    try {
      const data = await adelantosApi.getAll();
      setAdelantos(data);
      console.log(`✅ ${data.length} adelantos cargados`);
    } catch (error) {
      console.error("❌ Error cargando adelantos:", error);
    }
  };

  // Función para corregir sueldos corruptos en la base de datos
  const corregirSueldosCorruptos = async (mecanicos: any[]) => {
    try {
      const mecanicosCorrompidos = mecanicos.filter(m => isNaN(m.sueldo_base));
      
      if (mecanicosCorrompidos.length > 0) {
        console.log(`🔧 Corrigiendo ${mecanicosCorrompidos.length} sueldos corruptos...`);
        
        for (const mecanico of mecanicosCorrompidos) {
          await mecanicosApi.update(mecanico.id, { sueldo_base: 1500 });
        }
        
        console.log('✅ Sueldos corregidos exitosamente');
      }
    } catch (error) {
      console.error('Error corrigiendo sueldos:', error);
    }
  };

  const crearMecanicosEjemplo = async () => {
    const mecanicosEjemplo = [
      {
        nombre: 'Carlos Rodriguez',
        documento: '12345678',
        calificacion: 4.8,
        telefono: '+51 987 654 321',
        disponible: true,
        estado: 'activo',
        servicios_completados: 342,
        sueldo_base: 2500,
        ranking: 1,
        asistencias: generarAsistenciasEjemplo(28),
        dias_trabajados_mes: 28
      },
      {
        nombre: 'Ana Garcia',
        documento: '87654321',
        calificacion: 4.9,
        telefono: '+51 976 543 210',
        disponible: true,
        estado: 'activo',
        servicios_completados: 289,
        sueldo_base: 2200,
        ranking: 2,
        asistencias: generarAsistenciasEjemplo(26),
        dias_trabajados_mes: 26
      },
      {
        nombre: 'Luis Martinez',
        documento: '45678912',
        calificacion: 4.6,
        telefono: '+51 965 432 109',
        disponible: false,
        estado: 'activo',
        servicios_completados: 156,
        sueldo_base: 1800,
        ranking: 3,
        asistencias: generarAsistenciasEjemplo(24),
        dias_trabajados_mes: 24
      }
    ];

    try {
      for (const mecanico of mecanicosEjemplo) {
        await mecanicosApi.create(mecanico);
      }
      await cargarMecanicos();
    } catch (error) {
      console.error('Error creando mecánicos de ejemplo:', error);
    }
  };

  const generarAsistenciasEjemplo = (diasPresentes: number): { fecha: string; presente: boolean }[] => {
    const asistencias = [];
    const hoy = new Date();
    for (let i = 30; i >= 0; i--) {
      const fecha = new Date(hoy);
      fecha.setDate(fecha.getDate() - i);
      asistencias.push({
        fecha: fechaLima(fecha),
        presente: i <= diasPresentes
      });
    }
    return asistencias;
  };

  const calcularRanking = (mecanicosList: Mecanico[]): Mecanico[] => {
    // Calcular puntuación de ranking: servicios_completados * calificacion * (1 + experiencia/100)
    const mecanicosConPuntos = mecanicosList.map(m => ({
      ...m,
      puntos_ranking: m.servicios_completados * m.calificacion * (1 + (m.servicios_completados / 100))
    }));

    // Ordenar por puntos
    mecanicosConPuntos.sort((a, b) => b.puntos_ranking - a.puntos_ranking);

    // Asignar ranking
    return mecanicosConPuntos.map((m, index) => ({
      ...m,
      ranking: index + 1
    }));
  };

  const verificarAlertas = () => {
    const hoy = new Date();
    const dia = hoy.getDate();
    
    // Alerta quincena (día 15)
    if (dia === 15) {
      setMensajeAlerta('⚠️ RECORDATORIO: Hoy es día de pago quincenal para los mecánicos');
      setMostrarAlerta(true);
    }
    
    // Alerta fin de mes (últimos 3 días)
    const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).getDate();
    if (dia >= ultimoDia - 2) {
      setMensajeAlerta('⚠️ RECORDATORIO: Próximo pago mensual de sueldos');
      setMostrarAlerta(true);
    }
  };

  const agregarMecanico = async () => {
    if (!nuevoMecanico.nombre || !nuevoMecanico.telefono) {
      alert('Por favor complete todos los campos obligatorios');
      return;
    }
    if (!(Number(nuevoMecanico.sueldo_base) > 0)) {
      alert('Escribe el sueldo por quincena (mayor a 0)');
      return;
    }

    const mecanico = {
      nombre: nuevoMecanico.nombre,
      documento: nuevoMecanico.documento,
      telefono: nuevoMecanico.telefono,
      calificacion: nuevoMecanico.calificacion || 4.0,
      sueldo_base: nuevoMecanico.sueldo_base || 1500,
      servicios_completados: 0,
      estado: nuevoMecanico.estado || 'activo'
    };

    try {
      await mecanicosApi.create(mecanico);
      await cargarMecanicos();

      setNuevoMecanico({
        nombre: '',
        documento: '',
        telefono: '',
        calificacion: 4.0,
        disponible: true,
        estado: 'activo',
        servicios_completados: 0,
        sueldo_base: 1500
      });
      setMostrarFormulario(false);
      
      // Los datos se actualizarán automáticamente por la suscripción en tiempo real
    } catch (error) {
      console.error('Error agregando mecánico:', error);
      alert('Error al agregar mecánico: ' + (error as Error).message);
    }
  };

  // NUEVAS FUNCIONES DE EDICIÓN
  const editarMecanico = (mecanico: Mecanico) => {
    setEditandoMecanico(mecanico);
    setMostrarFormularioEdicion(true);
  };

  const actualizarMecanico = async () => {
    if (!editandoMecanico) {
      console.error('No hay mecánico para editar');
      return;
    }

    console.log('🔧 Actualizando mecánico:', {
      id: editandoMecanico.id,
      nombre: editandoMecanico.nombre,
      sueldo_nuevo: editandoMecanico.sueldo_base
    });

    try {
      // Validar sueldo_base
      const sueldoValidado = Number(editandoMecanico.sueldo_base);
      if (!(sueldoValidado > 0)) {
        alert('Escribe el sueldo por quincena (mayor a 0)');
        return;
      }
      
      const datosActualizacion = {
        nombre: editandoMecanico.nombre,
        documento: editandoMecanico.documento,
        telefono: editandoMecanico.telefono,
        calificacion: editandoMecanico.calificacion,
        sueldo_base: sueldoValidado,
        estado: editandoMecanico.estado,
        disponible: editandoMecanico.disponible
      };
      
      console.log('📊 Datos a enviar:', datosActualizacion);

      await mecanicosApi.update(editandoMecanico.id, datosActualizacion);

      console.log('✅ Actualización exitosa');

      // Actualizar estado local inmediatamente
      setMecanicos(prevMecanicos => 
        prevMecanicos.map(m => 
          m.id === editandoMecanico.id 
            ? { ...m, ...datosActualizacion }
            : m
        )
      );
      
      setEditandoMecanico(null);
      setMostrarFormularioEdicion(false);
      
      // Recargar datos después de un breve delay para asegurar sincronización
      setTimeout(() => {
        console.log('🔄 Recargando datos desde la base de datos...');
        cargarMecanicos();
      }, 1000);
      
    } catch (error) {
      console.error('❌ Error actualizando mecánico:', error);
      alert('Error al actualizar mecánico: ' + (error as Error).message);
    }
  };

  const eliminarMecanico = async (id: string, nombre: string) => {
    const confirmacion = window.confirm(`¿Estás seguro de eliminar al mecánico ${nombre}? Esta acción no se puede deshacer.`);
    
    if (!confirmacion) return;

    try {
      await mecanicosApi.delete(id);
      await cargarMecanicos();
    } catch (error) {
      console.error('Error eliminando mecánico:', error);
      alert('Error al eliminar mecánico: ' + (error as Error).message);
    }
  };

  const registrarAsistencia = async (mecanicoId: string, estado: 'presente' | 'ausente' | 'descanso') => {
    const hoy = fechaLima(new Date());
    
    try {
      const mecanico = mecanicos.find(m => m.id === mecanicoId);
      if (!mecanico) {
        alert('Mecánico no encontrado');
        return;
      }
      
      const asistencias = sanitizarAsistencias(mecanico.asistencias);
      const yaRegistrado = asistencias.some(a => a.fecha === hoy);
      
      let nuevasAsistencias;
      if (yaRegistrado) {
        // Actualizar registro existente
        nuevasAsistencias = asistencias.map(a => 
          a.fecha === hoy ? { fecha: hoy, presente: estado === 'presente', estado } : a
        );
      } else {
        // Agregar nuevo registro
        nuevasAsistencias = [...asistencias, { fecha: hoy, presente: estado === 'presente', estado }];
      }
      
      console.log('Actualizando asistencias:', {
        mecanicoId,
        nuevasAsistencias,
        timestamp: new Date().toISOString()
      });
      
      await mecanicosApi.update(mecanicoId, { asistencias: nuevasAsistencias });

      console.log('✅ Asistencia guardada, esperando propagación...');

      // Actualizar el estado local inmediatamente para UX rápida
      setMecanicos(prev => prev.map(m => 
        m.id === mecanicoId 
          ? { ...m, asistencias: nuevasAsistencias }
          : m
      ));
      
      alert(`✅ Asistencia registrada: ${estado === 'presente' ? 'Presente' : estado === 'descanso' ? 'Descanso' : 'Ausente'}`);
      
      // Recargar datos después de un momento para asegurar sincronización
      setTimeout(() => {
        console.log('🔄 Recargando datos para verificar sincronización...');
        cargarMecanicos();
      }, 1000);
      
    } catch (error) {
      console.error('Error registrando asistencia:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      alert('❌ Error al registrar asistencia: ' + errorMessage);
    }
  };

  const calcularSueldoTotal = (mecanico: Mecanico): number => {
    // Solo devolver el sueldo base, sin bonos
    const sueldoBase = isNaN(mecanico.sueldo_base) ? 1500 : mecanico.sueldo_base;
    return sueldoBase;
  };

  const obtenerMedalla = (ranking: number) => {
    if (ranking === 1) return { emoji: '🥇', color: 'text-yellow-500' };
    if (ranking === 2) return { emoji: '🥈', color: 'text-gray-400' };
    if (ranking === 3) return { emoji: '🥉', color: 'text-orange-600' };
    return { emoji: '', color: '' };
  };

  const calcularPorcentajeAsistencia = (mecanico: Mecanico): number => {
    const asistencias = sanitizarAsistencias(mecanico.asistencias);
    if (!asistencias || asistencias.length === 0) return 0;
    
    // Filtrar días de descanso para el cálculo
    // Estudio (2026-10-09) tampoco cuenta como dia laborable, igual que descanso
    const diasLaborables = asistencias.filter(a => a.estado !== 'descanso' && a.estado !== 'estudio');
    if (diasLaborables.length === 0) return 0;
    
    const presentes = diasLaborables.filter(a => a.estado === 'presente').length;
    return Math.round((presentes / diasLaborables.length) * 100);
  };

  const calcularDiasTrabajadosMes = (mecanico: Mecanico): number => {
    const asistencias = sanitizarAsistencias(mecanico.asistencias);
    if (!asistencias || asistencias.length === 0) return 0;
    
    // Obtener el mes actual
    const hoy = new Date();
    const mesActual = hoy.getFullYear() + '-' + String(hoy.getMonth() + 1).padStart(2, '0');
    
    // Filtrar asistencias del mes actual donde estuvo presente
    const diasTrabajadosMes = asistencias.filter(a => 
      a.fecha.startsWith(mesActual) && a.estado === 'presente'
    ).length;
    
    return diasTrabajadosMes;
  };

  // Funciones para el calendario mensual
  const obtenerDiasDelMes = (año: number, mes: number) => {
    const diasEnMes = new Date(año, mes + 1, 0).getDate();
    const dias = [];
    
    // Crear array con todos los días del mes
    for (let i = 1; i <= diasEnMes; i++) {
      dias.push(i);
    }
    
    return dias;
  };

  const obtenerAsistenciaDia = (mecanico: Mecanico, año: number, mes: number, dia: number) => {
    const fecha = `${año}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    const asistencias = sanitizarAsistencias(mecanico.asistencias);
    return asistencias.find(a => a.fecha === fecha);
  };

  const [fechaCalendario, setFechaCalendario] = useState(() => {
    const hoy = new Date();
    return { año: hoy.getFullYear(), mes: hoy.getMonth() };
  });

  const nombresMeses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const coloresSemana = [
    'bg-blue-100', 'bg-green-100', 'bg-yellow-100', 'bg-purple-100', 'bg-pink-100', 'bg-indigo-100'
  ];

  const toggleEstadoMecanico = async (mecanicoId: string) => {
    const mecanico = mecanicos.find(m => m.id === mecanicoId);
    if (!mecanico) return;

    const nuevoEstado = mecanico.estado === 'inactivo' ? 'activo' : 'inactivo';
    const accion = nuevoEstado === 'activo' ? 'reactivar' : 'dar de baja';
    
    if (!confirm(`¿Estás seguro de ${accion} a ${mecanico.nombre}?`)) {
      return;
    }

    try {
      await mecanicosApi.update(mecanicoId, { estado: nuevoEstado });

      alert(`Mecánico ${nuevoEstado === 'activo' ? 'reactivado' : 'dado de baja'} exitosamente`);
      cargarMecanicos();
    } catch (error) {
      console.error('Error cambiando estado del mecánico:', error);
      alert('Error al cambiar el estado del mecánico');
    }
  };

  // Filtrar mecánicos por búsqueda
  const mecanicosFiltrados = useMemo(() => {
    if (!busqueda.trim()) return mecanicos;
    
    return mecanicos.filter(mecanico =>
      mecanico.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
      mecanico.documento.toLowerCase().includes(busqueda.toLowerCase()) ||
      mecanico.telefono.toLowerCase().includes(busqueda.toLowerCase()) ||
      mecanico.estado.toLowerCase().includes(busqueda.toLowerCase())
    );
  }, [mecanicos, busqueda]);

  // ── Pagos de quincena ──────────────────────────────────────────────────────
  // sueldo_base es lo que gana el mecanico POR QUINCENA (asi lo usa el taller: Antony 900
  // por quincena). Cada falta descuenta lo que gana en un dia: quincena / 15.
  // 2026-10-15: antes dividia entre 30 (descontaba la mitad), contaba faltas del mes entero
  // con fechas en UTC y sumaba todos los adelantos de la historia del mecanico.
  const MESES_PAGO = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO',
    'SETIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];
  const [periodoPago, setPeriodoPago] = useState(() => {
    const [anio, mes, dia] = fechaLima().split('-').map(Number);
    return { anio, mes, mitad: (dia <= 15 ? 1 : 2) as 1 | 2 };
  });
  // ajustes a mano de la tabla (faltas, descuento, sueldo "solo esta quincena"): valen solo
  // para esa quincena y se recuerdan en esta PC (2026-10-15: antes se perdian al recargar).
  // Los adelantos y los aumentos de sueldo SI se guardan en el sistema (ventanas de abajo).
  type CampoPago = 'remuneracion' | 'faltas' | 'descuento';
  type AjustesPago = Record<string, Partial<Record<CampoPago, number>>>;
  const claveAjustes = (p: { anio: number; mes: number; mitad: number }) => `sr_ajustes_pago_${p.anio}_${p.mes}_${p.mitad}`;
  const leerAjustes = (p: { anio: number; mes: number; mitad: number }): AjustesPago => {
    try { return JSON.parse(localStorage.getItem(claveAjustes(p)) || '{}') || {}; } catch { return {}; }
  };
  const [ajustesPago, setAjustesPagoEstado] = useState<AjustesPago>(() => leerAjustes(periodoPago));
  const setAjustesPago = (cambio: AjustesPago | ((prev: AjustesPago) => AjustesPago)) => {
    setAjustesPagoEstado(prev => {
      const nuevo = typeof cambio === 'function' ? cambio(prev) : cambio;
      try { localStorage.setItem(claveAjustes(periodoPago), JSON.stringify(nuevo)); } catch { /* sin almacenamiento */ }
      return nuevo;
    });
  };
  const [versionCeldas, setVersionCeldas] = useState(0);   // para devolver una casilla a su valor
  const [ventanaAdelanto, setVentanaAdelanto] = useState<{ id: string; nombre: string } | null>(null);
  const [nuevoAdelantoPago, setNuevoAdelantoPago] = useState({ monto: '', fecha: '', caja: '', motivo: '' });
  const [guardandoPago, setGuardandoPago] = useState(false);
  const [ventanaSueldo, setVentanaSueldo] = useState<{ id: string; nombre: string; anterior: number; nuevo: number } | null>(null);
  // pagos de quincena ya registrados (salen de la Caja de Mantenimiento, total exacto)
  const [planillasPeriodo, setPlanillasPeriodo] = useState<any[]>([]);
  const [saldoMantenimiento, setSaldoMantenimiento] = useState<number | null>(null);
  const [ventanaAnularPago, setVentanaAnularPago] = useState<{ id: string; nombre: string; total: number } | null>(null);
  const [anulacion, setAnulacion] = useState({ clave: '', motivo: '' });

  const cambiarPeriodoPago = (cambios: Partial<typeof periodoPago>) => {
    const nuevo = { ...periodoPago, ...cambios };
    setPeriodoPago(nuevo);
    setAjustesPagoEstado(leerAjustes(nuevo));
  };
  const quitarAjuste = (id: string, campo: CampoPago) => setAjustesPago(prev => {
    const { [campo]: _quitado, ...resto } = prev[id] || {};
    const copia = { ...prev };
    if (Object.keys(resto).length) copia[id] = resto; else delete copia[id];
    return copia;
  });
  const ajustarPago = (id: string, campo: CampoPago, texto: string, actual: number) => {
    const valor = texto.trim() === '' ? 0 : parseFloat(texto);
    if (isNaN(valor) || valor < 0 || (campo === 'faltas' && !Number.isInteger(valor))) return false;
    if (valor === actual) return true;
    if (campo === 'remuneracion') {
      const f = filasPago.find(x => x.id === id);
      setVentanaSueldo({ id, nombre: f?.nombre || '', anterior: actual, nuevo: valor });
      return true;
    }
    setAjustesPago(prev => ({ ...prev, [id]: { ...prev[id], [campo]: valor } }));
    return true;
  };

  const mmPago = String(periodoPago.mes).padStart(2, '0');
  const ultimoDiaPago = new Date(periodoPago.anio, periodoPago.mes, 0).getDate();
  const desdePago = `${periodoPago.anio}-${mmPago}-${periodoPago.mitad === 1 ? '01' : '16'}`;
  const hastaPago = `${periodoPago.anio}-${mmPago}-${periodoPago.mitad === 1 ? '15' : ultimoDiaPago}`;
  const periodoPagoStr = `${periodoPago.anio}-${mmPago}-${periodoPago.mitad === 1 ? '01/15' : '16/fin'}`;   // mismo formato que adelantos.periodo
  const tituloPago = `PAGO DE LA ${periodoPago.mitad === 1 ? 'PRIMERA' : 'SEGUNDA'} QUINCENA DE ${MESES_PAGO[periodoPago.mes - 1]} ${periodoPago.anio}`;
  const fechaCorta = (f: string) => f.split('-').reverse().join('/');
  const hoyPago = fechaLima();
  const maxFechaAdelanto = hastaPago < hoyPago ? hastaPago : hoyPago;   // no se registran adelantos a futuro
  const adelantosDe = (id: string) => adelantos.filter(a => String(a.mecanico_id) === id && (a.periodo
    ? a.periodo === periodoPagoStr
    : String(a.fecha || '').slice(0, 10) >= desdePago && String(a.fecha || '').slice(0, 10) <= hastaPago));
  const NOMBRE_CAJA: Record<string, string> = { ventas: 'Caja de Repuestos', servicios: 'Caja de Servicios', mantenimiento: 'Caja de Mantenimiento' };

  const abrirVentanaAdelanto = (id: string, nombre: string) => {
    setNuevoAdelantoPago({ monto: '', fecha: maxFechaAdelanto, caja: '', motivo: '' });
    setVentanaAdelanto({ id, nombre });
  };
  const registrarAdelantoPago = async () => {
    if (!ventanaAdelanto) return;
    const monto = parseFloat(nuevoAdelantoPago.monto);
    if (!(monto > 0)) { alert('Ingresa un monto mayor a 0'); return; }
    if (nuevoAdelantoPago.fecha < desdePago || nuevoAdelantoPago.fecha > maxFechaAdelanto) {
      alert(`La fecha debe estar entre ${fechaCorta(desdePago)} y ${fechaCorta(maxFechaAdelanto)}`); return;
    }
    setGuardandoPago(true);
    try {
      await adelantosApi.create({ mecanico_id: ventanaAdelanto.id, monto, fecha: nuevoAdelantoPago.fecha,
        caja: 'mantenimiento', descripcion: nuevoAdelantoPago.motivo.trim() || null });
      await Promise.all([cargarAdelantos(), cargarPagosPeriodo()]);
      setNuevoAdelantoPago(n => ({ ...n, monto: '', motivo: '' }));
    } catch (e) {
      alert('No se pudo registrar el adelanto: ' + (e instanceof Error ? e.message : 'error'));
    } finally {
      setGuardandoPago(false);
    }
  };
  const borrarAdelantoPago = async (a: any) => {
    if (!confirm(`¿Borrar el adelanto de S/ ${a.monto} del ${fechaCorta(String(a.fecha).slice(0, 10))}? El dinero vuelve a la caja de la que salió.`)) return;
    try {
      await adelantosApi.delete(a.id);
      await Promise.all([cargarAdelantos(), cargarPagosPeriodo()]);
    } catch (e) {
      alert('No se pudo borrar: ' + (e instanceof Error ? e.message : 'error'));
    }
  };
  const guardarSueldoFijo = async () => {
    if (!ventanaSueldo) return;
    setGuardandoPago(true);
    try {
      await mecanicosApi.update(ventanaSueldo.id, { sueldo_base: ventanaSueldo.nuevo });
      quitarAjuste(ventanaSueldo.id, 'remuneracion');
      await cargarMecanicos();
      setVentanaSueldo(null);
    } catch (e) {
      alert('No se pudo guardar el sueldo: ' + (e instanceof Error ? e.message : 'error'));
    } finally {
      setGuardandoPago(false);
    }
  };
  const r2 = (n: number) => Math.round(n * 100) / 100;
  const fmtPago = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

  const filasPago = mecanicosFiltrados.filter(m => m.estado !== 'inactivo').map(m => {
    const id = String(m.id);
    const aj = ajustesPago[id] || {};
    const remuneracion = aj.remuneracion ?? (Number(m.sueldo_base) || 0);
    const adelanto = r2(adelantosDe(id).filter(a => !a.descontado).reduce((s, a) => s + (parseFloat(a.monto) || 0), 0));
    const faltas = aj.faltas ?? sanitizarAsistencias(m.asistencias)
      .filter(a => a.estado === 'ausente' && a.fecha >= desdePago && a.fecha <= hastaPago).length;
    const xDia = r2(remuneracion / 15);
    const descuento = aj.descuento ?? r2((remuneracion / 15) * faltas);
    const total = r2(remuneracion - adelanto - descuento);
    const redondeo = Math.round(total / 10) * 10;
    const pago = planillasPeriodo.find(pl => String(pl.mecanico_id) === id);
    if (pago) {
      const rem = Number(pago.remuneracion) || 0, tot = Number(pago.total) || 0;
      return { id, nombre: m.nombre, remuneracion: rem, adelanto: Number(pago.adelantos) || 0, faltas: Number(pago.faltas) || 0,
        xDia: r2(rem / 15), descuento: Number(pago.descuento_faltas) || 0, total: tot, redondeo: Math.round(tot / 10) * 10,
        ajustado: false, pago };
    }
    return { id, nombre: m.nombre, remuneracion, adelanto, faltas, xDia, descuento, total, redondeo, ajustado: Object.keys(aj).length > 0, pago: null as any };
  });
  // ── pagar la quincena: total exacto, desde la Caja de Mantenimiento ──
  const cargarPagosPeriodo = async () => {
    try {
      const [pl, saldo] = await Promise.all([planillasApi.getPagadosDelPeriodo(periodoPagoStr), cajaChicaApi.getSaldo('mantenimiento')]);
      setPlanillasPeriodo(pl || []);
      setSaldoMantenimiento(typeof saldo?.saldo === 'number' ? saldo.saldo : null);
    } catch (e) {
      console.error('Error cargando pagos de la quincena:', e);
    }
  };
  useEffect(() => {
    if (vistaActual === 'pagos') cargarPagosPeriodo();
  }, [vistaActual, periodoPagoStr]);
  const pagoDe = (id: string) => planillasPeriodo.find(p => String(p.mecanico_id) === id);
  const pagarFilas = async (filas: typeof filasPago) => {
    const pendientes = filas.filter(f => !pagoDe(f.id));
    if (pendientes.length === 0) return;
    const monto = r2(pendientes.reduce((s, f) => s + f.total, 0));
    if (pendientes.some(f => f.total < 0)) { alert('Hay un total negativo; revisa adelantos y descuentos antes de pagar'); return; }
    const quien = pendientes.length === 1 ? pendientes[0].nombre : `${pendientes.length} trabajadores`;
    const aviso = saldoMantenimiento !== null && monto > saldoMantenimiento
      ? `\n\n⚠ OJO: la Caja de Mantenimiento tiene S/ ${fmtPago(r2(saldoMantenimiento))}; quedaría en negativo.` : '';
    if (!confirm(`¿Pagar S/ ${fmtPago(monto)} a ${quien} (${tituloPago.toLowerCase()})?\n\nSale de la Caja de Mantenimiento (saldo S/ ${saldoMantenimiento !== null ? fmtPago(r2(saldoMantenimiento)) : '—'}).${aviso}`)) return;
    setGuardandoPago(true);
    try {
      const r = await planillasApi.guardar({
        periodo: periodoPagoStr, caja: 'mantenimiento',
        nomina: pendientes.map(f => ({ mecanico_id: f.id, nombre: f.nombre, remuneracion: f.remuneracion, faltas: f.faltas,
          descuento_faltas: f.descuento, total: f.total })),
      });
      await Promise.all([cargarPagosPeriodo(), cargarAdelantos()]);
      if (r.omitidos?.length) alert(`Ya estaban pagados: ${r.omitidos.join(', ')}`);
    } catch (e) {
      alert('No se pudo registrar el pago: ' + (e instanceof Error ? e.message : 'error'));
      cargarPagosPeriodo();
    } finally {
      setGuardandoPago(false);
    }
  };
  const anularPago = async () => {
    if (!ventanaAnularPago) return;
    if (!anulacion.clave || anulacion.motivo.trim().length < 3) { alert('Escribe la clave de administrador y el motivo'); return; }
    setGuardandoPago(true);
    try {
      await planillasApi.anular(ventanaAnularPago.id, { clave: anulacion.clave, motivo: anulacion.motivo.trim() });
      setVentanaAnularPago(null);
      setAnulacion({ clave: '', motivo: '' });
      await Promise.all([cargarPagosPeriodo(), cargarAdelantos()]);
    } catch (e) {
      alert('No se pudo anular: ' + (e instanceof Error ? e.message : 'error'));
    } finally {
      setGuardandoPago(false);
    }
  };

  const totalesPago = filasPago.reduce((t, f) => ({
    remuneracion: t.remuneracion + f.remuneracion, adelanto: t.adelanto + f.adelanto, faltas: t.faltas + f.faltas,
    descuento: t.descuento + f.descuento, total: t.total + f.total, redondeo: t.redondeo + f.redondeo,
  }), { remuneracion: 0, adelanto: 0, faltas: 0, descuento: 0, total: 0, redondeo: 0 });

  return (
    <div className="p-6">
      {/* Alerta de pagos */}
      {mostrarAlerta && (
        <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Bell className="h-5 w-5 text-yellow-400 mr-3" />
              <p className="text-yellow-800 font-medium">{mensajeAlerta}</p>
            </div>
            <button
              onClick={() => setMostrarAlerta(false)}
              className="text-yellow-400 hover:text-yellow-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestión de Mecánicos</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setMostrarAsistencia(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-700"
          >
            <CheckCircle className="w-4 h-4" />
            <span>Registrar Asistencia</span>
          </button>
          <button
            onClick={() => setMostrarFormulario(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            <span>Agregar Mecánico</span>
          </button>
        </div>
      </div>

      {/* Barra de búsqueda */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar mecánicos por nombre, documento, teléfono o estado..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
      
      {/* Pestañas de navegación */}
      <div className="mb-6 border-b">
        <nav className="flex space-x-4">
          <button
            onClick={() => setVistaActual('lista')}
            className={`pb-3 px-2 font-medium ${
              vistaActual === 'lista'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Lista General
          </button>
          <button
            onClick={() => setVistaActual('adelantos')}
            className={`pb-3 px-2 font-medium ${
              vistaActual === 'adelantos'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Adelantos
          </button>
          <button
            onClick={() => setVistaActual('asistencia')}
            className={`pb-3 px-2 font-medium ${
              vistaActual === 'asistencia'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Asistencia
          </button>
          <button
            onClick={() => setVistaActual('calendario')}
            className={`pb-3 px-2 font-medium ${
              vistaActual === 'calendario'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Calendario Mensual
          </button>
          <button
            onClick={() => setVistaActual('pagos')}
            className={`pb-3 px-2 font-medium ${
              vistaActual === 'pagos'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Pagos
          </button>
        </nav>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Total Mecánicos</p>
              <p className="text-2xl font-bold">{mecanicosFiltrados.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Presentes Hoy</p>
              <p className="text-2xl font-bold">
                {(() => {
                  const fechaHoy = fechaLima(new Date());
                  return mecanicosFiltrados.filter(m => m.estado === 'activo').filter(mecanico => {
                    const asistencias = sanitizarAsistencias(mecanico.asistencias);
                    return asistencias.some(a => a.fecha === fechaHoy && a.estado === 'presente');
                  }).length;
                })()}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <Trophy className="h-8 w-8 text-yellow-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Mejor Mecánico</p>
              <p className="text-lg font-bold">{mecanicos[0]?.nombre.split(' ')[0] || 'N/A'}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Total Sueldos/Mes</p>
              <p className="text-2xl font-bold">
                S/ {mecanicos.reduce((sum, m) => sum + calcularSueldoTotal(m), 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Vista: Lista General */}
      {vistaActual === 'lista' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold">Listado de Mecánicos</h2>
          </div>
          <div className="divide-y">
            {mecanicosFiltrados.map((mecanico) => {
              const medalla = obtenerMedalla(mecanico.ranking);
              const estaInactivo = mecanico.estado === 'inactivo';
              return (
                <div key={mecanico.id} className={`p-6 hover:bg-gray-50 ${estaInactivo ? 'opacity-50 bg-gray-50' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        mecanico.disponible ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        <Users className={`w-6 h-6 ${
                          mecanico.disponible ? 'text-green-600' : 'text-gray-400'
                        }`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{mecanico.nombre}</h3>
                          {medalla.emoji && (
                            <span className="text-xl">{medalla.emoji}</span>
                          )}
                          <span className="text-sm text-gray-500">#{mecanico.ranking}</span>
                          {estaInactivo && (
                            <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full font-medium">
                              INACTIVO
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span>{mecanico.telefono}</span>
                        </div>
                        <p className="text-sm text-gray-500">
                          {mecanico.servicios_completados} servicios completados
                        </p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="flex items-center space-x-1 mb-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                        <span className="font-medium">{mecanico.calificacion}</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {mecanico.servicios_completados} servicios
                      </p>
                      <p className="text-sm font-medium text-green-600">
                        S/ {calcularSueldoTotal(mecanico).toLocaleString()}
                      </p>
                      <div className="mt-2 flex flex-col gap-2">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          mecanico.disponible 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {mecanico.disponible ? 'Disponible' : 'Ocupado'}
                        </span>
                        
                        {/* Botones de Edición */}
                        <div className="flex gap-1">
                          <button
                            onClick={() => editarMecanico(mecanico)}
                            className="flex-1 px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs font-medium transition-colors flex items-center justify-center"
                            title="Editar mecánico"
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Editar
                          </button>
                          <button
                            onClick={() => eliminarMecanico(mecanico.id, mecanico.nombre)}
                            className="flex-1 px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-medium transition-colors flex items-center justify-center"
                            title="Eliminar mecánico"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Eliminar
                          </button>
                        </div>
                        
                        <button
                          onClick={() => toggleEstadoMecanico(mecanico.id)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            estaInactivo
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-orange-500 hover:bg-orange-600 text-white'
                          }`}
                        >
                          {estaInactivo ? 'Reactivar' : 'Dar de Baja'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Vista: Adelantos */}
      {vistaActual === 'adelantos' && (
        <div className="bg-white rounded-lg shadow p-6">
          <iframe 
            src="/adelantos" 
            className="w-full" 
            style={{ height: 'calc(100vh - 250px)', border: 'none' }}
            title="Adelantos de Mecánicos"
          />
        </div>
      )}
      {/* Vista: Asistencia */}
      {vistaActual === 'asistencia' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Control de Asistencia</h2>
                <p className="text-sm text-gray-600">Registro de asistencia del mes actual</p>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${
                  estadoConexion === 'conectado' ? 'bg-green-500' : 
                  estadoConexion === 'sincronizando' ? 'bg-yellow-500 animate-pulse' : 
                  'bg-red-500'
                }`}></div>
                <span className="text-xs text-gray-500">
                  {estadoConexion === 'conectado' && 'Sincronizado'}
                  {estadoConexion === 'sincronizando' && 'Sincronizando...'}
                  {estadoConexion === 'desconectado' && 'Desconectado'}
                </span>
                <span className="text-xs text-gray-400">
                  {ultimaActualizacion.toLocaleTimeString()}
                </span>
                <button
                  onClick={() => {
                    console.log('🔄 Sincronización manual iniciada...');
                    setEstadoConexion('sincronizando');
                    cargarMecanicos().then(() => {
                      setUltimaActualizacion(new Date());
                      setEstadoConexion('conectado');
                    });
                  }}
                  className="text-blue-600 hover:text-blue-800 text-xs underline"
                >
                  Actualizar
                </button>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Mecánico
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Días Trabajados
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    % Asistencia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Última Asistencia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mecanicosFiltrados.map((mecanico) => {
                  const porcentaje = calcularPorcentajeAsistencia(mecanico);
                  return (
                    <tr key={mecanico.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{mecanico.nombre}</div>
                        <div className="text-sm text-gray-500">{mecanico.documento}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium">{calcularDiasTrabajadosMes(mecanico)} días</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className={`h-2 rounded-full ${
                                porcentaje >= 90 ? 'bg-green-500' :
                                porcentaje >= 70 ? 'bg-yellow-500' :
                                'bg-red-500'
                              }`}
                              style={{ width: `${porcentaje}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium">{porcentaje}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {mecanico.ultima_asistencia || 'Sin registro'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          porcentaje >= 90 ? 'bg-green-100 text-green-800' :
                          porcentaje >= 70 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {porcentaje >= 90 ? 'Excelente' :
                           porcentaje >= 70 ? 'Regular' :
                           'Bajo'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Vista: Pagos */}
      {vistaActual === 'pagos' && (
        <div className="bg-white rounded-lg shadow">
          <div className="flex flex-wrap items-center gap-3 p-4 border-b">
            <span className="text-sm font-medium text-gray-700">Quincena:</span>
            <select value={periodoPago.mitad} onChange={(e) => cambiarPeriodoPago({ mitad: Number(e.target.value) as 1 | 2 })}
              className="border rounded-lg px-3 py-2">
              <option value={1}>Primera (1 al 15)</option>
              <option value={2}>Segunda (16 al {ultimoDiaPago})</option>
            </select>
            <select value={periodoPago.mes} onChange={(e) => cambiarPeriodoPago({ mes: Number(e.target.value) })}
              className="border rounded-lg px-3 py-2">
              {MESES_PAGO.map((m, i) => <option key={m} value={i + 1}>{m.charAt(0) + m.slice(1).toLowerCase()}</option>)}
            </select>
            <select value={periodoPago.anio} onChange={(e) => cambiarPeriodoPago({ anio: Number(e.target.value) })}
              className="border rounded-lg px-3 py-2">
              {[periodoPago.anio - 1, periodoPago.anio, periodoPago.anio + 1].map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <span className="ml-auto text-sm text-gray-700">
              Caja de Mantenimiento: <b className={saldoMantenimiento !== null && saldoMantenimiento < 0 ? 'text-red-600' : ''}>
                {saldoMantenimiento !== null ? `S/ ${fmtPago(r2(saldoMantenimiento))}` : '—'}</b>
            </span>
            {filasPago.some(f => !f.pago) && (
              <button onClick={() => pagarFilas(filasPago)} disabled={guardandoPago}
                className="px-4 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-50">
                Pagar a todos ({filasPago.filter(f => !f.pago).length}) · S/ {fmtPago(r2(filasPago.filter(f => !f.pago).reduce((s, f) => s + f.total, 0)))}
              </button>
            )}
            {filasPago.some(f => f.ajustado) && (
              <button onClick={() => setAjustesPago({})} className="px-3 py-2 rounded-lg bg-yellow-100 text-yellow-800 hover:bg-yellow-200 text-sm">
                Quitar ajustes a mano
              </button>
            )}
          </div>
          <div className="p-6 border-b bg-red-700 text-white text-center">
            <h2 className="text-2xl font-bold">{tituloPago}</h2>
            <p className="text-sm mt-1 text-red-100">
              Del {fechaCorta(desdePago)} al {fechaCorta(hastaPago)} · Descuento por falta = quincena ÷ 15 (lo que gana en un día)
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-red-700 text-white">
                  <th className="border border-gray-400 px-3 py-2 text-sm font-bold">Nro</th>
                  <th className="border border-gray-400 px-3 py-2 text-sm font-bold">Nombres y Apellido</th>
                  <th className="border border-gray-400 px-3 py-2 text-sm font-bold">REMUNERACIÓN</th>
                  <th className="border border-gray-400 px-3 py-2 text-sm font-bold">ADELANTO</th>
                  <th className="border border-gray-400 px-3 py-2 text-sm font-bold">X DIA</th>
                  <th className="border border-gray-400 px-3 py-2 text-sm font-bold">FALTAS</th>
                  <th className="border border-gray-400 px-3 py-2 text-sm font-bold">DESCUENTO</th>
                  <th className="border border-gray-400 px-3 py-2 text-sm font-bold">TOTAL</th>
                  <th className="border border-gray-400 px-3 py-2 text-sm font-bold">REDONDEO</th>
                  <th className="border border-gray-400 px-3 py-2 text-sm font-bold">PAGO</th>
                </tr>
              </thead>
              <tbody>
                {filasPago.map((f, index) => {
                  // celda que se puede corregir a mano; el TOTAL se recalcula solo
                  const celda = (campo: CampoPago, valor: number, clase: string, titulo: string) => f.pago ? (
                    <td className={`border border-gray-300 px-3 py-2 text-center font-bold ${clase}`}>{valor > 0 ? fmtPago(valor) : ''}</td>
                  ) : (
                    <td className={`border border-gray-300 px-1 py-1 text-center font-bold ${clase}`}>
                      <input
                        key={`${f.id}-${campo}-${valor}-${periodoPagoStr}-${versionCeldas}`}
                        type="number" min="0" step={campo === 'faltas' ? 1 : 0.01}
                        defaultValue={valor > 0 ? fmtPago(valor) : ''}
                        onBlur={(e) => { if (!ajustarPago(f.id, campo, e.currentTarget.value, valor)) e.currentTarget.value = valor > 0 ? fmtPago(valor) : ''; }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                        title={titulo}
                        className="w-24 bg-transparent text-center font-bold focus:bg-white focus:outline focus:outline-2 focus:outline-blue-400 rounded"
                      />
                    </td>
                  );
                  return (
                    <tr key={f.id} className={f.pago ? 'bg-green-50' : f.ajustado ? 'bg-yellow-50' : 'hover:bg-gray-50'}>
                      <td className="border border-gray-300 px-3 py-2 text-center bg-red-600 text-white font-bold">
                        {index + 1}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 bg-green-700 text-white font-semibold">
                        {f.nombre}{f.ajustado && <span title="Tiene ajustes a mano en esta quincena"> ✎</span>}
                      </td>
                      {celda('remuneracion', f.remuneracion, '', 'Sueldo de la quincena. Al cambiarlo pregunta si es un aumento fijo o solo para esta quincena')}
                      <td className="border border-gray-300 px-1 py-1 text-center font-bold text-red-600">
                        <button onClick={() => abrirVentanaAdelanto(f.id, f.nombre)} title={f.pago ? 'Ver adelantos (ya descontados en el pago)' : 'Registrar o ver adelantos de esta quincena'}
                          className="w-24 rounded hover:bg-red-50 py-1">
                          {f.adelanto > 0 ? fmtPago(f.adelanto) : <span className="text-gray-400 font-normal">+ adelanto</span>}
                        </button>
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center">{fmtPago(f.xDia)}</td>
                      {celda('faltas', f.faltas, 'text-red-600', 'Faltas de esta quincena (se puede corregir)')}
                      {celda('descuento', f.descuento, 'text-red-600', 'Descuento por faltas = X DIA × FALTAS (se puede corregir)')}
                      <td className="border border-gray-300 px-3 py-2 text-center font-bold">{fmtPago(f.total)}</td>
                      <td className="border border-gray-300 px-3 py-2 text-center bg-gray-200 font-bold">{f.redondeo}</td>
                      <td className="border border-gray-300 px-2 py-1 text-center whitespace-nowrap">
                        {f.pago ? (
                          <div className="text-xs">
                            <span className="text-green-700 font-bold">✓ Pagado</span>
                            <span className="text-gray-500"> {new Date(f.pago.created_at).toLocaleDateString('es-PE', { timeZone: 'America/Lima', day: '2-digit', month: '2-digit' })}</span>
                            <button onClick={() => { setAnulacion({ clave: '', motivo: '' }); setVentanaAnularPago({ id: f.pago.id, nombre: f.nombre, total: f.total }); }}
                              className="ml-2 text-red-600 hover:underline">Anular</button>
                          </div>
                        ) : (
                          <button onClick={() => pagarFilas([f])} disabled={guardandoPago || f.total < 0}
                            className="px-3 py-1 rounded bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50">
                            Pagar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* FILA DE TOTAL AL FINAL */}
              <tfoot>
                <tr className="bg-blue-600 text-white font-bold">
                  <td className="border border-gray-400 px-3 py-3 text-center" colSpan={2}>
                    TOTAL A PAGAR
                  </td>
                  <td className="border border-gray-400 px-3 py-3 text-center">{fmtPago(r2(totalesPago.remuneracion))}</td>
                  <td className="border border-gray-400 px-3 py-3 text-center text-red-300">{fmtPago(r2(totalesPago.adelanto))}</td>
                  <td className="border border-gray-400 px-3 py-3 text-center">-</td>
                  <td className="border border-gray-400 px-3 py-3 text-center text-red-300">{totalesPago.faltas}</td>
                  <td className="border border-gray-400 px-3 py-3 text-center text-red-300">{fmtPago(r2(totalesPago.descuento))}</td>
                  <td className="border border-gray-400 px-3 py-3 text-center text-green-300 text-xl">{fmtPago(r2(totalesPago.total))}</td>
                  <td className="border border-gray-400 px-3 py-3 text-center bg-yellow-400 text-blue-900 text-2xl font-extrabold">{totalesPago.redondeo}</td>
                  <td className="border border-gray-400 px-3 py-3 text-center text-sm">
                    {filasPago.filter(f => f.pago).length}/{filasPago.length} pagados
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="px-4 py-3 text-xs text-gray-500">
            PAGO: "Pagar" registra el pago del TOTAL exacto y lo descuenta de la Caja de Mantenimiento (también "Pagar a todos"); los adelantos de la quincena quedan descontados. Un pago por error se anula con la clave de administrador y el dinero vuelve a la caja.
            ADELANTO: toca la casilla para registrar un adelanto (sale de la Caja de Mantenimiento y se guarda en el sistema).
            REMUNERACIÓN: al cambiarla se pregunta si es un aumento de sueldo fijo o solo para esta quincena.
            FALTAS y DESCUENTO: se pueden corregir a mano (Enter para aplicar); valen solo para esta quincena y se recuerdan en esta PC.
            Las faltas de verdad se marcan en Asistencia. Las filas corregidas a mano quedan en amarillo.
          </p>

          {ventanaAdelanto && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
                <div className="flex justify-between items-start mb-1">
                  <h3 className="text-lg font-bold">Adelantos de {ventanaAdelanto.nombre}</h3>
                  <button onClick={() => setVentanaAdelanto(null)} className="text-gray-500 hover:text-gray-800"><X className="h-5 w-5" /></button>
                </div>
                <p className="text-sm text-gray-600 mb-4">{periodoPago.mitad === 1 ? 'Primera' : 'Segunda'} quincena: del {fechaCorta(desdePago)} al {fechaCorta(hastaPago)}</p>

                {adelantosDe(ventanaAdelanto.id).length > 0 ? (
                  <table className="w-full text-sm mb-4">
                    <tbody>
                      {adelantosDe(ventanaAdelanto.id).map(a => (
                        <tr key={a.id} className="border-b">
                          <td className="py-1">{fechaCorta(String(a.fecha).slice(0, 10))}</td>
                          <td className="py-1 font-bold text-red-600">S/ {fmtPago(parseFloat(a.monto) || 0)}</td>
                          <td className="py-1 text-gray-600">{NOMBRE_CAJA[a.caja] || ''}{a.motivo ? ` · ${a.motivo}` : ''}</td>
                          <td className="py-1 text-right">
                            {a.descontado
                              ? <span className="text-xs text-gray-500">ya descontado</span>
                              : <button onClick={() => borrarAdelantoPago(a)} className="text-red-600 hover:text-red-800" title="Borrar (devuelve el dinero a la caja)"><Trash2 className="h-4 w-4" /></button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-gray-500 mb-4">No tiene adelantos en esta quincena.</p>
                )}

                {filasPago.find(x => x.id === ventanaAdelanto.id)?.pago ? (
                  <p className="text-sm text-green-800 bg-green-50 rounded p-3">Esta quincena ya se le pagó. Un adelanto nuevo se registra en la quincena siguiente.</p>
                ) : maxFechaAdelanto < desdePago ? (
                  <p className="text-sm text-yellow-800 bg-yellow-50 rounded p-3">Esta quincena todavía no empieza: el adelanto se registra desde el {fechaCorta(desdePago)}.</p>
                ) : (
                  <div className="space-y-3 border-t pt-4">
                    <p className="font-semibold text-sm">Registrar adelanto</p>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="text-sm">Monto (S/)
                        <input type="number" min="0" step="0.01" value={nuevoAdelantoPago.monto} autoFocus
                          onChange={(e) => setNuevoAdelantoPago(n => ({ ...n, monto: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-2 mt-1" />
                      </label>
                      <label className="text-sm">Fecha
                        <input type="date" min={desdePago} max={maxFechaAdelanto} value={nuevoAdelantoPago.fecha}
                          onChange={(e) => setNuevoAdelantoPago(n => ({ ...n, fecha: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-2 mt-1" />
                      </label>
                    </div>
                    <p className="text-sm text-gray-600">Sale de la <b>Caja de Mantenimiento</b>{saldoMantenimiento !== null ? ` (saldo S/ ${fmtPago(r2(saldoMantenimiento))})` : ''}.</p>
                    <label className="text-sm block">Motivo (opcional)
                      <input type="text" value={nuevoAdelantoPago.motivo} maxLength={120}
                        onChange={(e) => setNuevoAdelantoPago(n => ({ ...n, motivo: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2 mt-1" />
                    </label>
                    <button onClick={registrarAdelantoPago} disabled={guardandoPago}
                      className="w-full bg-red-600 text-white rounded-lg py-2 font-semibold hover:bg-red-700 disabled:opacity-50">
                      {guardandoPago ? 'Guardando…' : 'Registrar adelanto'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {ventanaAnularPago && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                <h3 className="text-lg font-bold mb-2">Anular pago de {ventanaAnularPago.nombre}</h3>
                <p className="text-sm text-gray-700 mb-4">
                  Se anula el pago de <b>S/ {fmtPago(ventanaAnularPago.total)}</b> ({tituloPago.toLowerCase()}). El dinero vuelve a la Caja de Mantenimiento
                  y sus adelantos de esta quincena vuelven a quedar pendientes. Queda en Auditoría.
                </p>
                <label className="text-sm block mb-3">Clave de administrador
                  <input type="password" value={anulacion.clave} onChange={(e) => setAnulacion(a => ({ ...a, clave: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1" autoFocus />
                </label>
                <label className="text-sm block mb-4">Motivo
                  <input type="text" value={anulacion.motivo} maxLength={200} onChange={(e) => setAnulacion(a => ({ ...a, motivo: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1" />
                </label>
                <div className="flex gap-2">
                  <button onClick={anularPago} disabled={guardandoPago}
                    className="flex-1 bg-red-600 text-white rounded-lg py-2 font-semibold hover:bg-red-700 disabled:opacity-50">
                    {guardandoPago ? 'Anulando…' : 'Anular pago'}
                  </button>
                  <button onClick={() => setVentanaAnularPago(null)} className="flex-1 border rounded-lg py-2 text-gray-700 hover:bg-gray-50">Cancelar</button>
                </div>
              </div>
            </div>
          )}

          {ventanaSueldo && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                <h3 className="text-lg font-bold mb-2">Sueldo de {ventanaSueldo.nombre}</h3>
                <p className="text-sm text-gray-700 mb-4">
                  Cambiar de <b>S/ {fmtPago(ventanaSueldo.anterior)}</b> a <b>S/ {fmtPago(ventanaSueldo.nuevo)}</b> por quincena. ¿Qué es este cambio?
                </p>
                <div className="space-y-2">
                  <button onClick={guardarSueldoFijo} disabled={guardandoPago}
                    className="w-full bg-green-600 text-white rounded-lg py-2 font-semibold hover:bg-green-700 disabled:opacity-50">
                    {guardandoPago ? 'Guardando…' : 'Aumento de sueldo (queda fijo desde ahora)'}
                  </button>
                  <button onClick={() => { setAjustesPago(prev => ({ ...prev, [ventanaSueldo.id]: { ...prev[ventanaSueldo.id], remuneracion: ventanaSueldo.nuevo } })); setVentanaSueldo(null); }}
                    className="w-full bg-yellow-100 text-yellow-900 rounded-lg py-2 font-semibold hover:bg-yellow-200">
                    Solo para esta quincena
                  </button>
                  <button onClick={() => { setVentanaSueldo(null); setVersionCeldas(v => v + 1); }}
                    className="w-full border rounded-lg py-2 text-gray-700 hover:bg-gray-50">
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {vistaActual === 'calendario' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold">Calendario de Asistencia Mensual</h2>
                <p className="text-sm text-gray-600">Control visual de asistencia por mecánico</p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setFechaCalendario(prev => ({
                    año: prev.mes === 0 ? prev.año - 1 : prev.año,
                    mes: prev.mes === 0 ? 11 : prev.mes - 1
                  }))}
                  className="px-3 py-1 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  ← Anterior
                </button>
                <div className="text-lg font-semibold">
                  {nombresMeses[fechaCalendario.mes]} {fechaCalendario.año}
                </div>
                <button
                  onClick={() => setFechaCalendario(prev => ({
                    año: prev.mes === 11 ? prev.año + 1 : prev.año,
                    mes: prev.mes === 11 ? 0 : prev.mes + 1
                  }))}
                  className="px-3 py-1 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Siguiente →
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Leyenda */}
            <div className="flex items-center justify-center space-x-6 mb-6 text-sm">
              <div className="flex items-center">
                <span className="text-green-600 font-bold text-lg mr-2">✓</span>
                <span>Presente</span>
              </div>
              <div className="flex items-center">
                <span className="text-red-600 font-bold text-lg mr-2">✗</span>
                <span>Ausente</span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-400 font-bold text-lg mr-2">-</span>
                <span>Sin registro</span>
              </div>
            </div>

            {/* Tabla estilo Excel */}
            <div className="overflow-x-auto border border-gray-300 rounded-lg">
              <table className="min-w-full bg-white">
                {/* Encabezado principal con semanas */}
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="p-3 text-left bg-gray-100 border-r border-gray-300 font-bold text-sm sticky left-0 z-10">
                      ASISTENCIA DE {nombresMeses[fechaCalendario.mes].toUpperCase()}
                    </th>
                    {(() => {
                      const dias = obtenerDiasDelMes(fechaCalendario.año, fechaCalendario.mes);
                      const semanas = [];
                      let semanaActual = 1;
                      let diasEnSemana = 0;
                      let inicioSemana = 0;

                      for (let i = 0; i < dias.length; i++) {
                        if (diasEnSemana === 0) {
                          inicioSemana = i;
                        }
                        diasEnSemana++;
                        
                        // Cambiar de semana cada 7 días o al final del mes
                        if (diasEnSemana === 7 || i === dias.length - 1) {
                          semanas.push({
                            numero: semanaActual,
                            inicio: inicioSemana,
                            fin: i,
                            diasCount: diasEnSemana,
                            color: coloresSemana[(semanaActual - 1) % coloresSemana.length]
                          });
                          semanaActual++;
                          diasEnSemana = 0;
                        }
                      }

                      return semanas.map(semana => (
                        <th 
                          key={semana.numero}
                          colSpan={semana.diasCount}
                          className={`p-2 text-center font-bold text-sm ${semana.color} border-r border-gray-300`}
                        >
                          SEMANA {semana.numero}
                        </th>
                      ));
                    })()}
                  </tr>
                  
                  {/* Fila con los números de los días */}
                  <tr className="border-b border-gray-300 bg-gray-50">
                    <th className="p-3 text-center bg-gray-100 border-r border-gray-300 font-bold text-sm sticky left-0 z-10">
                      EMPLEADO
                    </th>
                    {obtenerDiasDelMes(fechaCalendario.año, fechaCalendario.mes).map(dia => {
                      const hoy = new Date();
                      const esHoy = hoy.getDate() === dia && 
                                   hoy.getMonth() === fechaCalendario.mes && 
                                   hoy.getFullYear() === fechaCalendario.año;
                      
                      return (
                        <th 
                          key={dia}
                          className={`p-2 text-center font-bold text-sm border-r border-gray-300 w-12 ${
                            esHoy ? 'bg-blue-200 text-blue-800' : 'bg-gray-50'
                          }`}
                        >
                          {dia}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                {/* Cuerpo de la tabla con los mecánicos */}
                <tbody>
                  {mecanicosFiltrados.filter(m => m.estado === 'activo').map((mecanico, index) => (
                    <tr key={mecanico.id} className={`border-b border-gray-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      {/* Columna del nombre del mecánico */}
                      <td className="p-3 border-r border-gray-300 font-medium text-sm bg-gray-100 sticky left-0 z-10">
                        <div className="text-gray-900">{mecanico.nombre}</div>
                        <div className="text-xs text-gray-500">
                          Asistencia: {calcularPorcentajeAsistencia(mecanico)}%
                        </div>
                      </td>
                      
                      {/* Columnas de los días */}
                      {obtenerDiasDelMes(fechaCalendario.año, fechaCalendario.mes).map(dia => {
                        const asistencia = obtenerAsistenciaDia(mecanico, fechaCalendario.año, fechaCalendario.mes, dia);
                        const hoy = new Date();
                        const esHoy = hoy.getDate() === dia && 
                                     hoy.getMonth() === fechaCalendario.mes && 
                                     hoy.getFullYear() === fechaCalendario.año;

                        return (
                          <td 
                            key={dia}
                            className={`p-2 text-center border-r border-gray-200 w-12 h-12 ${
                              esHoy ? 'bg-blue-50' : ''
                            }`}
                            title={`${mecanico.nombre} - ${dia}/${fechaCalendario.mes + 1}: ${
                              asistencia ? (asistencia.estado === 'estudio' ? 'Estudio' : asistencia.estado === 'descanso' ? 'Descanso' : asistencia.presente ? 'Presente' : 'Ausente') : 'Sin registro'
                            }`}
                          >
                            {asistencia ? (
                              asistencia.estado === 'descanso' ? (
                                <span className="text-yellow-600 font-bold text-lg">D</span>
                              ) : asistencia.estado === 'estudio' ? (
                                <span className="text-violet-600 font-bold text-lg">E</span>
                              ) : asistencia.presente ? (
                                <span className="text-green-600 font-bold text-lg">✓</span>
                              ) : (
                                <span className="text-red-600 font-bold text-lg">✗</span>
                              )
                            ) : (
                              <span className="text-gray-400 font-bold">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Resumen estadístico */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border">
                <h3 className="font-semibold text-green-800">Total Presente</h3>
                <p className="text-2xl font-bold text-green-600">
                  {(() => {
                    const { año, mes } = fechaCalendario;
                    const diasDelMes = obtenerDiasDelMes(año, mes);
                    return mecanicosFiltrados.filter(m => m.estado === 'activo').reduce((total, mecanico) => {
                      const asistencias = sanitizarAsistencias(mecanico.asistencias);
                      return total + diasDelMes.filter(dia => {
                        const fecha = `${año}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
                        const asistencia = asistencias.find(a => a.fecha === fecha);
                        return asistencia && asistencia.estado === 'presente';
                      }).length;
                    }, 0);
                  })()}
                </p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg border">
                <h3 className="font-semibold text-yellow-800">Total Descanso</h3>
                <p className="text-2xl font-bold text-yellow-600">
                  {(() => {
                    const { año, mes } = fechaCalendario;
                    const diasDelMes = obtenerDiasDelMes(año, mes);
                    return mecanicosFiltrados.filter(m => m.estado === 'activo').reduce((total, mecanico) => {
                      const asistencias = sanitizarAsistencias(mecanico.asistencias);
                      return total + diasDelMes.filter(dia => {
                        const fecha = `${año}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
                        const asistencia = asistencias.find(a => a.fecha === fecha);
                        return asistencia && asistencia.estado === 'descanso';
                      }).length;
                    }, 0);
                  })()}
                </p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border">
                <h3 className="font-semibold text-red-800">Total Ausente</h3>
                <p className="text-2xl font-bold text-red-600">
                  {(() => {
                    const { año, mes } = fechaCalendario;
                    const diasDelMes = obtenerDiasDelMes(año, mes);
                    return mecanicosFiltrados.filter(m => m.estado === 'activo').reduce((total, mecanico) => {
                      const asistencias = sanitizarAsistencias(mecanico.asistencias);
                      return total + diasDelMes.filter(dia => {
                        const fecha = `${año}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
                        const asistencia = asistencias.find(a => a.fecha === fecha);
                        return asistencia && asistencia.estado === 'ausente';
                      }).length;
                    }, 0);
                  })()}
                </p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border">
                <h3 className="font-semibold text-blue-800">Promedio Asistencia</h3>
                <p className="text-2xl font-bold text-blue-600">
                  {(() => {
                    const mecanicosActivos = mecanicosFiltrados.filter(m => m.estado === 'activo');
                    if (mecanicosActivos.length === 0) return 0;
                    
                    const { año, mes } = fechaCalendario;
                    const diasDelMes = obtenerDiasDelMes(año, mes);
                    let totalPresente = 0;
                    let totalLaborable = 0;
                    
                    mecanicosActivos.forEach(mecanico => {
                      const asistencias = sanitizarAsistencias(mecanico.asistencias);
                      diasDelMes.forEach(dia => {
                        const fecha = `${año}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
                        const asistencia = asistencias.find(a => a.fecha === fecha);
                        if (asistencia && asistencia.estado !== 'descanso') {
                          totalLaborable++;
                          if (asistencia.estado === 'presente') {
                            totalPresente++;
                          }
                        }
                      });
                    });
                    
                    return totalLaborable > 0 ? Math.round((totalPresente / totalLaborable) * 100) : 0;
                  })()}%
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Agregar Mecánico */}
      {mostrarFormulario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Agregar Nuevo Mecánico</h2>
              <button
                onClick={() => setMostrarFormulario(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  value={nuevoMecanico.nombre}
                  onChange={(e) => setNuevoMecanico({...nuevoMecanico, nombre: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Ej: Carlos Rodriguez"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Documento de Identidad *
                </label>
                <input
                  type="text"
                  value={nuevoMecanico.documento}
                  onChange={(e) => setNuevoMecanico({...nuevoMecanico, documento: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Ej: 12345678"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono *
                </label>
                <input
                  type="text"
                  value={nuevoMecanico.telefono}
                  onChange={(e) => setNuevoMecanico({...nuevoMecanico, telefono: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="+51 987 654 321"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sueldo por quincena (S/)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={nuevoMecanico.sueldo_base || ''}
                  onChange={(e) => setNuevoMecanico({...nuevoMecanico, sueldo_base: parseFloat(e.target.value) || 0})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setMostrarFormulario(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={agregarMecanico}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Agregar Mecánico
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Registrar Asistencia */}
      {mostrarAsistencia && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold">Registrar Asistencia</h2>
                <p className="text-sm text-gray-600">Fecha: {new Date().toLocaleDateString('es-PE')}</p>
              </div>
              <button
                onClick={() => setMostrarAsistencia(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {mecanicosFiltrados.filter(m => m.estado !== 'inactivo').map((mecanico) => (
                <div key={mecanico.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">{mecanico.nombre}</p>
                    <p className="text-sm text-gray-600">{mecanico.telefono}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        registrarAsistencia(mecanico.id, 'presente');
                        alert(`Asistencia registrada para ${mecanico.nombre}`);
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      ✓ Presente
                    </button>
                    <button
                      onClick={() => {
                        registrarAsistencia(mecanico.id, 'descanso');
                        alert(`Descanso registrado para ${mecanico.nombre}`);
                      }}
                      className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
                    >
                      D Descanso
                    </button>
                    <button
                      onClick={() => {
                        registrarAsistencia(mecanico.id, 'ausente');
                        alert(`Ausencia registrada para ${mecanico.nombre}`);
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      ✗ Ausente
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setMostrarAsistencia(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición */}
      {mostrarFormularioEdicion && editandoMecanico && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold flex items-center">
                <Edit className="mr-2 h-5 w-5" />
                Editar Mecánico
              </h2>
              <button
                onClick={() => {
                  setMostrarFormularioEdicion(false);
                  setEditandoMecanico(null);
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); actualizarMecanico(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={editandoMecanico.nombre}
                  onChange={(e) => setEditandoMecanico({...editandoMecanico, nombre: e.target.value})}
                  required
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Documento
                </label>
                <input
                  type="text"
                  value={editandoMecanico.documento}
                  onChange={(e) => setEditandoMecanico({...editandoMecanico, documento: e.target.value})}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  value={editandoMecanico.telefono}
                  onChange={(e) => setEditandoMecanico({...editandoMecanico, telefono: e.target.value})}
                  required
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Calificación
                </label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  step="0.1"
                  value={editandoMecanico.calificacion}
                  onChange={(e) => setEditandoMecanico({...editandoMecanico, calificacion: parseFloat(e.target.value)})}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Sueldo por quincena (S/)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editandoMecanico.sueldo_base || ''}
                  onChange={(e) => {
                    const nuevoSueldo = parseFloat(e.target.value) || 0;
                    setEditandoMecanico({...editandoMecanico, sueldo_base: nuevoSueldo});
                  }}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Estado
                </label>
                <select
                  value={editandoMecanico.estado}
                  onChange={(e) => setEditandoMecanico({...editandoMecanico, estado: e.target.value})}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                  <option value="vacaciones">Vacaciones</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Disponibilidad
                </label>
                <select
                  value={editandoMecanico.disponible ? 'disponible' : 'ocupado'}
                  onChange={(e) => setEditandoMecanico({...editandoMecanico, disponible: e.target.value === 'disponible'})}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="disponible">Disponible</option>
                  <option value="ocupado">Ocupado</option>
                </select>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setMostrarFormularioEdicion(false);
                    setEditandoMecanico(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center"
                >
                  <Save className="mr-2 h-4 w-4" />
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MecanicosCompleto;
